export const asDetailText = (value, fallback = '-') => {
  if (value === undefined || value === null || value === '') return fallback;
  if (Array.isArray(value)) return value.filter(Boolean).map((entry) => asDetailText(entry, '')).filter(Boolean).join(', ') || fallback;
  if (typeof value === 'object') {
    if (value.summary || value.description || value.value || value.name) {
      return value.summary || value.description || value.value || value.name;
    }
    const text = Object.entries(value)
      .filter(([, entry]) => entry !== undefined && entry !== null && entry !== '')
      .map(([key, entry]) => `${key}: ${asDetailText(entry, '')}`)
      .filter((entry) => !entry.endsWith(': '))
      .join(' / ');
    return text || fallback;
  }
  return String(value);
};

const pick = (...values) => values.find((value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
});

const nested = (value, path) => path.reduce((current, key) => current?.[key], value);
const normalizePresetId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized || 'telecom';
};

const FINANCE_DNA_LABELS = {
  investmentProfile: '투자 성향 요약',
  appAndChannelUsage: '앱·채널 사용 맥락',
  stockDiscoveryStyle: '종목 발굴 방식',
  decisionAutonomy: '의사결정 자율성',
  investmentProductScope: '투자 상품 범위',
  brokerLoyaltyVsCostOptimization: '증권사·비용 전략',
  assetIntegrationPreference: '자산 통합 조회 성향',
  tradingExecutionStyle: '매매 실행 방식',
  aiRecommendationTrust: 'AI·추천 신뢰',
  directVsIndirectPreference: '직접·간접 투자 선호',
  socialAndFlowSignalUsage: '사회·수급 신호 활용',
  householdInvestmentContext: '가족·계좌 맥락',
  performanceBenchmarkOrientation: '성과 평가 기준',
  personalizationAndAlertPreference: '개인화·알림 니즈',
};

/**
 * Domain variable labels (key → Korean).
 * TO-BE formal presets (transport/card/insurance/lifestyle) + AS-IS aliases for older personas.
 */
const DOMAIN_VARIABLE_LABELS = {
  ...FINANCE_DNA_LABELS,

  // transport (TO-BE)
  travelProfile: '이동 성향',
  bookingChannelHabit: '예매 채널 습관',
  bookingTimingStyle: '예매 시점·확보 전략',
  itineraryFlexibility: '일정 유연성',
  valueAndDiscountOrientation: '요금·할인·가성비 성향',
  changeCancelFriction: '변경·취소 부담',
  certaintyAndAlertNeeds: '확실성·알림 니즈',
  companionContext: '동행·공유 맥락',
  trustAndOfficialPrefer: '신뢰·공식 채널 선호',
  uxControlPreference: '조작·통제 선호',
  // transport AS-IS aliases
  seatAndFarePreference: '좌석·요금 선호',
  alertAndCertaintyNeeds: '확실성·알림 니즈',
  ticketSharingBehavior: '동행·공유 맥락',
  firstLastMileLinkage: '첫·막 마일 연계',
  peakSeasonStrategy: '성수기·명절 전략',
  trustAndAuthoritySignals: '신뢰·공식 채널 선호',

  // card (TO-BE)
  cardUsageProfile: '카드 이용 성향',
  issuanceTrigger: '발급 계기',
  benefitDiscoveryStyle: '혜택 발견·탐색',
  comparisonCriteria: '비교·선택 기준',
  payAndAppHabit: '결제·앱 이용 습관',
  rewardOrientation: '포인트·리워드 태도',
  conditionTrustSensitivity: '조건·혜택 신뢰 민감도',
  switchWillingness: '카드 교체 의향',
  householdSpendContext: '가구·소비·고정비 맥락',
  // card AS-IS aliases
  appAndPayChannelRoles: '결제·앱 이용 습관',
  pointsAndRewardAttitude: '포인트·리워드 태도',
  subscriptionBillingNeeds: '가구·소비·고정비 맥락',
  benefitTrustSensitivity: '조건·혜택 신뢰 민감도',
  paymentFrictionTolerance: '결제 마찰 허용도',
  eventMessageResponse: '조건·혜택 신뢰 민감도',
  cardSwitchWillingness: '카드 교체 의향',

  // insurance (TO-BE)
  insuranceProfile: '보험 이용 성향',
  searchAndChannelPath: '탐색·채널 경로',
  comparisonStyle: '비교·결정 방식',
  comprehensionAndAnxiety: '이해·고지 불안',
  processFriction: '가입 과정 마찰',
  productStructureSense: '상품 구조 감각',
  priceTransparencyNeeds: '가격·조건 투명성',
  trustAndSafetyNeeds: '신뢰·안전 니즈',
  lifeAndCoverageContext: '보장·라이프 맥락',
  // insurance AS-IS aliases
  entryAndSearchPath: '탐색·채널 경로',
  channelPreference: '탐색·채널 경로',
  termComprehensionNeeds: '이해·고지 불안',
  disclosureAnxiety: '이해·고지 불안',
  formAndAuthFriction: '가입 과정 마찰',
  renewalAndRiderSense: '상품 구조 감각',
  premiumTransparencyNeeds: '가격·조건 투명성',
  dropoutTriggers: '가입 과정 마찰',
  trustAndSafetySignals: '신뢰·안전 니즈',
  lifeEventContext: '보장·라이프 맥락',

  // lifestyle / 생활(가전) (TO-BE)
  homeApplianceProfile: '가전 이용·관심 성향',
  buyVsSubscribeDecision: '구매 vs 구독 결정',
  discoveryAndResearchPath: '탐색·리서치 경로',
  careAndManagementValue: '관리·케어 가치',
  valueAndDealOrientation: '가성비·할인 성향',
  brandLoyaltyAndSwitch: '브랜드 신뢰·전환',
  replacementCadence: '교체 리듬',
  featureNoveltySeeking: '신기능 추구',
  householdInfluence: '가구 영향 구조',
  riskAndContractAnxiety: '계약·리스크 불안',
  // lifestyle AS-IS aliases
  careAndMaintenanceValue: '관리·케어 가치',
  discountStackingBehavior: '가성비·할인 성향',
  brandLockInDynamics: '브랜드 신뢰·전환',
  subscriptionRetentionDrivers: '구매 vs 구독 결정',
  channelPurchaseSplit: '탐색·리서치 경로',
};

const humanizeVariableKey = (key, labelByKey = {}) => {
  if (labelByKey && labelByKey[key]) return labelByKey[key];
  const known = DOMAIN_VARIABLE_LABELS[key];
  if (known) return known;
  const spaced = String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced || key;
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Prefer live schema labels from generation/company template when present. */
export function extractDomainLabelMap(persona = {}) {
  const map = {};
  const candidates = [
    pick(persona.domainSchema, persona.domain_schema),
    pick(persona.personaSchema, persona.persona_schema),
    nested(persona, ['sourceData', 'domainSchema']),
    nested(persona, ['source_data', 'domainSchema']),
  ].filter(isPlainObject);

  for (const schema of candidates) {
    const vars = pick(schema.variables, schema.Variables);
    if (!Array.isArray(vars)) continue;
    for (const item of vars) {
      if (!isPlainObject(item)) continue;
      const key = String(item.key || '').trim();
      const label = String(item.label || '').trim();
      if (key && label) map[key] = label;
    }
  }
  return map;
}

const buildFinanceDimensionSections = (financeDomain, persona) => Object.entries(FINANCE_DNA_LABELS).map(([key, label]) => [
  label,
  pick(
    persona[key],
    financeDomain[key],
    financeDomain[key.replace(/[A-Z]/g, (char, index) => `${index ? '_' : ''}${char.toLowerCase()}`)],
    persona.typeAppUsage && key === 'appAndChannelUsage' && !['All-in-one', 'Multi-App'].includes(String(persona.typeAppUsage))
      ? persona.typeAppUsage
      : null,
  ),
]);

/**
 * Flatten domain variable maps (Explore domain_notes, custom schemas) into
 * [label, value] rows for the detail UI.
 */
export function buildDomainCharacteristicItems(domainMap, labelByKey = {}) {
  if (!isPlainObject(domainMap)) return [];
  return Object.entries(domainMap)
    .filter(([key, value]) => {
      if (!key || key.startsWith('_')) return false;
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (isPlainObject(value) && Object.keys(value).length === 0) return false;
      return true;
    })
    .map(([key, value]) => [humanizeVariableKey(key, labelByKey), value]);
}

export function buildEditableDomainCharacteristicItems(domainMap, labelByKey = {}, onChange) {
  if (!isPlainObject(domainMap)) return [];
  return Object.entries(domainMap)
    .filter(([key, value]) => {
      if (!key || key.startsWith('_')) return false;
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (isPlainObject(value) && Object.keys(value).length === 0) return false;
      return true;
    })
    .map(([key, value]) => [
      humanizeVariableKey(key, labelByKey),
      value,
      (nextValue) => onChange(key, nextValue),
    ]);
}

export function resolvePersonaDetailMode(persona = {}) {
  const sourceType = String(pick(persona.sourceType, persona.source_type) || '').toLowerCase();
  const sourceData = pick(persona.sourceData, persona.source_data) || {};
  const domainVariables = pick(persona.domainVariables, persona.domain_variables) || {};
  const exploreNotes = pick(
    domainVariables.explore,
    sourceData.domain_notes,
    sourceData.domainNotes
  );
  const isExplore = sourceType === 'explore'
    || sourceData.explore === true
    || (isPlainObject(exploreNotes) && Object.keys(exploreNotes).length > 0);

  if (isExplore) return 'domain';

  const presetId = normalizePresetId(
    pick(
      persona.presetId,
      persona.preset_id,
      persona.companyPresetId,
      persona.company_preset_id,
      nested(persona, ['domainVariables', 'presetId'])
    )
  );
  if (presetId === 'finance') return 'finance';
  return 'telecom';
}

export function extractDomainCharacteristicsMap(persona = {}) {
  const sourceData = pick(persona.sourceData, persona.source_data) || {};
  const domainVariables = pick(persona.domainVariables, persona.domain_variables) || {};
  const exploreNotes = pick(
    domainVariables.explore,
    sourceData.domain_notes,
    sourceData.domainNotes
  );
  if (isPlainObject(exploreNotes) && Object.keys(exploreNotes).length > 0) {
    return exploreNotes;
  }

  const financeNotes = domainVariables.finance;
  if (isPlainObject(financeNotes) && Object.keys(financeNotes).length > 0) {
    return financeNotes;
  }

  // Formal domain templates (transport/card/…) nest string DNA under their template id.
  if (!isPlainObject(domainVariables)) return {};
  const reserved = new Set(['finance', 'telecom', 'explore', 'presetId', 'preset_id']);
  for (const [key, value] of Object.entries(domainVariables)) {
    if (reserved.has(key) || !isPlainObject(value)) continue;
    const stringEntries = Object.entries(value).filter(([, entry]) => typeof entry === 'string' && entry.trim());
    if (stringEntries.length >= 1) {
      return Object.fromEntries(stringEntries);
    }
  }
  return {};
}

export function getPersonaRolePresentation(persona) {
  const profile = persona?.profile || {};
  const roleInfo = {
    title: pick(persona?.title, profile.job, profile.occupation, persona?.occupation),
    sector: pick(persona?.sector, profile.sector, profile.industry, persona?.industry),
    roleArea: pick(persona?.roleArea, profile.roleArea, profile.role, profile.responsibilities, persona?.role),
  };
  const labels = {
    title: '직함',
    sector: '산업',
    roleArea: '직무 영역',
  };
  const items = [
    ['title', roleInfo.title],
    ['sector', roleInfo.sector],
    ['roleArea', roleInfo.roleArea],
  ].map(([key, value]) => [labels[key], typeof value === 'string' ? value.trim() : value]);

  return {
    sectionTitle: '직업 정보',
    items,
    labels,
  };
}

const TELECOM_DIMENSION_SECTIONS = [
  {
    key: 'brandRetention',
    title: '브랜드 유지 성향',
    description: '브랜드를 유지하거나 프리미엄 인프라와 혜택을 중시하는 기준입니다.',
    items: [
      ['브랜드 유지 성향', 'brandRetentionTendency'],
      ['프리미엄 인프라 및 혜택 지향', 'premiumInfraBenefitOrientation'],
    ],
  },
  {
    key: 'optimizationResource',
    title: '최적화 리소스 투입',
    description: '비용 절감과 시간/노력 투입 사이에서 어떤 선택을 하는지 보여줍니다.',
    items: [
      ['최적화 리소스 투입(금전 ↔ 시간/노력)', 'optimizationResourceInvestment'],
      ['비용 지불 저항선', 'paymentResistanceLine'],
    ],
  },
  {
    key: 'informationControl',
    title: '정보 탐색 및 통제 욕구',
    description: '정보를 탐색하고 문제를 해결할 때 얼마나 직접 통제하려 하는지 나타냅니다.',
    items: [
      ['정보 탐색 양식', 'informationExplorationStyle'],
      ['문제 해결 경로의 자율성', 'problemSolvingAutonomy'],
    ],
  },
  {
    key: 'digitalAiOpenness',
    title: '디지털 및 AI 개방성',
    description: 'AI, 공급자 추천, 개인화 데이터 제공을 받아들이는 태도입니다.',
    items: [
      ['AI 및 공급자 신뢰도', 'aiProviderTrust'],
      ['개인화 수용 및 정보제공 범위', 'personalizationDataSharingScope'],
    ],
  },
  {
    key: 'telecomLifeCharacteristics',
    title: '변수에 따른 통신 생활 특성',
    description: '가구 의사결정, 상품 이해도, 실제 통신 서비스 이용 맥락을 연결합니다.',
    items: [
      ['가구 내 및 통신 의사결정 주도권', 'householdDecisionLeadership'],
      ['상품 및 서비스 이해도', 'productServiceUnderstanding'],
      ['통신사 제공 서비스 이용 맥락', 'telecomServiceUsageContext'],
    ],
  },
];

const TELECOM_SCORE_CONFIG = [
  {
    key: 'brandRetention',
    label: '브랜드 유지 성향',
    color: '#00cfc0',
    positive: ['브랜드', '유지', '장기', '신뢰', '안정', '안도', '검증', '대형', '프리미엄', '품질', '인프라', '결합', 'loyal', 'retention', 'premium', 'trusted', 'stable'],
    negative: ['알뜰폰', '번호이동', '변경', '이탈', '저가', '가격 우선', 'brand switch', 'switch'],
  },
  {
    key: 'optimizationResource',
    label: '최적화 리소스 투입',
    color: '#9ad167',
    positive: ['직접 비교', '비교', '검토', '분석', '확인', '재검토', '후보', '시간을 쓰', '발품', '관리', '절감', 'compare', 'review', 'optimize', 'manage'],
    negative: ['위임', '맡기', '알아서', '귀찮', '번거', '시간이 아깝', '관심이 없다', '결론만', '헤매기보다', '추천에 전적', 'delegate', 'hands off', 'too much effort'],
  },
  {
    key: 'informationControl',
    label: '정보탐색 및 통제 욕구',
    color: '#ff7c6a',
    positive: ['직접', '탐색', '검색', '검증', '근거', '비교표', '조건', '스스로', '자율', '통제', '확인', '대조', 'control', 'verify', 'evidence', 'search'],
    negative: ['위임', '추천', '전문가', '결론만', '알아서', '관심이 없다', '해독하려 들지', '맡기', 'delegate'],
  },
  {
    key: 'digitalAiOpenness',
    label: '디지털 및 AI 개방성',
    color: '#c083f6',
    positive: ['AI', '인공지능', '디지털', '앱', '자동', '추천', '개인화', '데이터 제공', '데이터까지 허용', '구독', 'ChatGPT', '제미나이', 'Gemini', 'DX', 'digital', 'personalization'],
    negative: ['거부', '불신', '대면', '오프라인만', '정보 제공을 꺼', '데이터 제공 거부', '꺼림', 'distrust'],
  },
];

const pickDimensionGroup = (dimensions, groupKey) =>
  pick(dimensions?.[groupKey], dimensions?.[groupKey.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]) || {};

export function buildTelecomDimensionSections(dimensions) {
  return TELECOM_DIMENSION_SECTIONS.map((section) => {
    const group = pickDimensionGroup(dimensions, section.key);
    return {
      key: section.key,
      title: section.title,
      description: section.description,
      items: section.items.map(([label, key]) => [
        label,
        pick(group?.[key], group?.[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]),
      ]),
    };
  });
}

const collectText = (...values) => values.flatMap((value) => {
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  if (Array.isArray(value)) return collectText(...value);
  if (value && typeof value === 'object') return collectText(...Object.values(value));
  return [];
}).join('\n');

const keywordHits = (text, keywords) => {
  const lowered = String(text || '').toLowerCase();
  return keywords.filter((keyword) => keyword && lowered.includes(keyword.toLowerCase()));
};

const scoreFromKeywords = (text, positive, negative) => {
  const positiveHits = keywordHits(text, positive);
  const negativeHits = keywordHits(text, negative);
  let score = 3;
  if (positiveHits.length >= 4) score += 2;
  else if (positiveHits.length >= 2) score += 1;
  if (negativeHits.length >= 4) score -= 2;
  else if (negativeHits.length >= 2) score -= 1;
  return {
    score: Math.max(1, Math.min(5, score)),
    basis: {
      positiveSignals: positiveHits.slice(0, 3),
      negativeSignals: negativeHits.slice(0, 3),
    },
  };
};

export function buildTelecomBehaviorScores(persona = {}, dimensions = null) {
  const existing = Array.isArray(persona.telecomBehaviorScores)
    ? persona.telecomBehaviorScores
    : Array.isArray(persona.telecom_behavior_scores)
      ? persona.telecom_behavior_scores
      : [];
  if (existing.length > 0) {
    return TELECOM_SCORE_CONFIG.map((config) => {
      const score = existing.find((item) => item?.key === config.key || item?.label === config.label) || {};
      return {
        ...config,
        ...score,
        score: Math.max(1, Math.min(5, Number(score.score) || 3)),
        maxScore: Number(score.maxScore || score.max_score || 5),
      };
    });
  }

  const profile = persona.profile || {};
  const telecom = persona.telecom_profile || {};
  const telecomDimensions = dimensions || persona.telecomBehaviorDimensions || telecom.telecomBehaviorDimensions || {};
  const telecomLife = pickDimensionGroup(telecomDimensions, 'telecomLifeCharacteristics');
  const sharedContext = collectText(
    profile.preferences,
    profile.behaviours,
    profile.behavior_pattern,
    persona.preferences,
    persona.behaviours,
    persona.personality,
    persona.motivation,
    persona.culturalBackground,
    profile.culturalBackground,
    profile.cultural_background
  );
  if (!Object.keys(telecomDimensions || {}).length && !sharedContext) return [];
  const contextByKey = {
    brandRetention: collectText(pickDimensionGroup(telecomDimensions, 'brandRetention'), telecomLife, sharedContext),
    optimizationResource: collectText(pickDimensionGroup(telecomDimensions, 'optimizationResource'), telecomLife, sharedContext),
    informationControl: collectText(pickDimensionGroup(telecomDimensions, 'informationControl'), telecomLife, sharedContext),
    digitalAiOpenness: collectText(
      pickDimensionGroup(telecomDimensions, 'digitalAiOpenness'),
      persona.uxInteraction,
      persona.telecomUsage,
      telecom,
      sharedContext
    ),
  };

  return TELECOM_SCORE_CONFIG.map((config) => ({
    ...config,
    maxScore: 5,
    ...scoreFromKeywords(contextByKey[config.key], config.positive, config.negative),
  }));
}

export function buildPersonaDetail(persona) {
  if (!persona) return null;
  const profile = persona.profile || {};
  const telecom = persona.telecom_profile || {};
  const detailMode = resolvePersonaDetailMode(persona);
  const presetId = normalizePresetId(
    pick(
      persona.presetId,
      persona.preset_id,
      persona.companyPresetId,
      persona.company_preset_id,
      nested(persona, ['domainVariables', 'presetId'])
    )
  );
  const detailProfile = persona.detail_profile || profile.detail_profile || {};
  const behavior = persona.behavior || profile.behavior || {};
  const telecomUsage = pick(persona.telecomUsage, telecom.telecomUsage);
  const telecomValues = pick(persona.telecomValues, telecom.telecomValues);
  const uxInteraction = pick(persona.uxInteraction, telecom.uxInteraction);
  const telecomDimensions = pick(persona.telecomBehaviorDimensions, telecom.telecomBehaviorDimensions);
  const rolePresentation = getPersonaRolePresentation(persona);
  const quote = pick(persona.quote, profile.quote);
  const telecomDimensionSections = buildTelecomDimensionSections(telecomDimensions);
  const financeDomain = pick(
    nested(persona, ['domainVariables', 'finance']),
    persona.domainVariables,
    persona.domain_variables
  ) || {};
  const financeDimensions = buildFinanceDimensionSections(financeDomain, persona);
  const domainCharacteristics = buildDomainCharacteristicItems(
    extractDomainCharacteristicsMap(persona),
    extractDomainLabelMap(persona),
  );

  return {
    presetId,
    detailMode,
    domainCharacteristics,
    domainCharacteristicsTitle: '도메인 특성',
    quote,
    meta: [
      pick(persona.age, profile.age) ? `${pick(persona.age, profile.age)}세` : '',
      pick(persona.gender, profile.gender),
      pick(persona.title, persona.occupation, profile.job, profile.occupation),
    ].filter(Boolean).join(' / '),
    basic: [
      ['나이', pick(persona.age, profile.age)],
      ['세대', pick(persona.generation, profile.generation)],
      ['성별', pick(persona.gender, profile.gender)],
      ['거주', pick(persona.currentCity, profile.currentCity, profile.location, profile.residence, persona.location)],
      ['국가', pick(persona.currentCountry, profile.currentCountry)],
      ['연소득', pick(persona.income, profile.income, profile.annual_income)],
    ],
    roleSectionTitle: rolePresentation.sectionTitle,
    job: rolePresentation.items,
    telecom: [
      ['통신 이용 요약', telecomUsage],
      ['통신 가치관', telecomValues],
      ['UX 상호작용', uxInteraction],
      [
        '통신서비스 이용 맥락',
        pick(
          nested(telecomDimensions, ['telecomLifeCharacteristics', 'telecomServiceUsageContext']),
          nested(telecomDimensions, ['telecom_service_usage_context']),
          nested(telecomUsage, ['telecomServiceUsageContext'])
        ),
      ],
    ],
    telecomDimensionSections,
    telecomLifeCharacteristicsSection: telecomDimensionSections.find((section) => section.key === 'telecomLifeCharacteristics'),
    telecomBehaviorScores: buildTelecomBehaviorScores(persona, telecomDimensions),
    financeDimensions,
    personality: [
      ['성격', pick(persona.personality, profile.personality)],
      ['행동 패턴', pick(persona.behaviours, profile.behaviours, persona.behavior_pattern, profile.behavior_pattern, behavior.pattern)],
      ['동기부여', pick(persona.motivation, profile.motivation)],
      ['가치관/태도', pick(persona.attitudes, profile.attitudes, profile.values, profile.attitude, persona.values)],
    ],
    detailProfile: [
      ['문화적 배경', pick(persona.culturalBackground, profile.culturalBackground, detailProfile.cultural_background, profile.cultural_background)],
      ['성장 배경', pick(persona.upbringing, profile.upbringing, detailProfile.growth_background, profile.growth_background)],
      ['이력', pick(persona.biography, profile.biography, detailProfile.history, profile.history, persona.history)],
      ['거주이력', pick(persona.locations, profile.locations, detailProfile.residence_history, profile.residence_history)],
      ['관심사/취미', pick(persona.interests, profile.interests, detailProfile.interests)],
      ['인맥/관계', pick(persona.socialContext, profile.socialContext, detailProfile.relationships, profile.relationships)],
      ['선호도', pick(persona.preferences, profile.preferences, detailProfile.preferences)],
      ['말투/스타일', pick(persona.demeanour, profile.demeanour, detailProfile.speech_style, profile.speech_style, persona.speech_style)],
      ['대표 발화', pick(persona.quote, profile.quote)],
    ],
  };
}
